import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
} from "@-/com.npmjs.tanstack__react-query";
import arrayLength from "@-/com.npmjs.extra-array__length";
import useLatest from "@-/com.npmjs.use-latest";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  morphemeDetailsTileSliceQueryFn,
  useGetMorphemeDetailsTileSliceQueryKey,
} from "#get-morpheme-details-tile-slice";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Morphemes revealed per `revealMore`. ~10 rows of the 4-column phone grid, and comfortably under
 * the 64-lookup DataLoader batch the slice read is bounded to — so one page is one batched DB read.
 */
export const MORPHEME_CARDS_PAGE_SIZE = 40;

type MorphemeCardsPage = {
  offset: number;
  slices: (MorphemeDetailsTileSlice | null)[];
};

const EMPTY_CARDS: ReadonlyMap<string, MorphemeDetailsTileSlice | null> = new Map();

export type RevealedMorphemeCards = {
  /** Resolved cards for the revealed window, in `morphemeList` order. */
  cards: MorphemeDetailsTileSlice[];
  /** More morphemes are still hidden — render a "more below" footer and wire `revealMore`. */
  hasMore: boolean;
  /** The revealed window still has cards resolving. */
  isPending: boolean;
  /** Grow the revealed window by one page (clamped to the list length). Stable identity. */
  revealMore: () => void;
};

/**
 * Details-button cards for an ordered morpheme list, resolved a page at a time.
 *
 * For a list that can be long, resolving a card for every entry up front (one `useQueries` entry
 * each) and blocking the whole screen until ALL of them settle is what made a broad gloss —
 * "place name" matches 153 characters, and the empty gloss 657 — sit on a spinner while hundreds
 * of queries drained. Nothing is truncated: `revealMore` keeps growing the window until every
 * morpheme is rendered.
 *
 * Cards are keyed by morpheme rather than paired positionally, so a changing list can never show
 * one morpheme's card under another's glyph.
 *
 * `morphemeList` must already be ordered and de-duplicated.
 */
export function useRevealedMorphemeCards({
  enabled = true,
  morphemeList,
}: {
  /** Gate the reads (e.g. an unfocused screen) — reveal state is kept either way. */
  enabled?: boolean;
  morphemeList: readonly string[];
}): RevealedMorphemeCards {
  const [revealedCount, setRevealedCount] = useState(MORPHEME_CARDS_PAGE_SIZE);
  const totalCount = arrayLength(morphemeList);
  const visibleCount = Math.min(revealedCount, totalCount);

  const getMorphemeDetailsTileSliceQueryKey = useGetMorphemeDetailsTileSliceQueryKey();
  const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isFetchNextPageError } =
    useInfiniteQuery(
      infiniteQueryOptions({
        enabled: enabled && totalCount > 0,
        queryFn: async ({ client, pageParam }): Promise<MorphemeCardsPage> => {
          const pageMorphemes = morphemeList.slice(pageParam, pageParam + MORPHEME_CARDS_PAGE_SIZE);
          const slices = await Promise.all(
            pageMorphemes.map((morpheme) =>
              client.fetchQuery(
                queryOptions({
                  queryFn: morphemeDetailsTileSliceQueryFn,
                  queryKey: getMorphemeDetailsTileSliceQueryKey({ morpheme }),
                }),
              ),
            ),
          );
          return { offset: pageParam, slices };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage: MorphemeCardsPage): number | undefined => {
          const nextOffset = lastPage.offset + MORPHEME_CARDS_PAGE_SIZE;
          return nextOffset >= totalCount ? undefined : nextOffset;
        },
        // The key getter is a per-render closure over the DB-scoped loader; it hashes to `{}` so a
        // new identity never invalidates the sequence, but listing it keeps the dependency explicit.
        queryKey: [
          "morphemeCards",
          { getMorphemeDetailsTileSliceQueryKey, morphemeList },
        ] as const,
      }),
    );

  const loadedCount = useMemo(
    () => data?.pages.reduce((count, page) => count + arrayLength(page.slices), 0) ?? 0,
    [data],
  );

  // Follow the revealed window: one page at a time, never past a failed page.
  useEffect(() => {
    if (
      enabled &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isError &&
      !isFetchNextPageError &&
      loadedCount < visibleCount
    ) {
      void fetchNextPage();
    }
  }, [
    enabled,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    loadedCount,
    visibleCount,
  ]);

  const cardByMorpheme = useMemo(() => {
    if (data === undefined) {
      return EMPTY_CARDS;
    }
    const map = new Map<string, MorphemeDetailsTileSlice | null>();
    for (const page of data.pages) {
      for (const [index, slice] of page.slices.entries()) {
        const morpheme = morphemeList[page.offset + index];
        if (morpheme !== undefined) {
          map.set(morpheme, slice);
        }
      }
    }
    return map;
  }, [data, morphemeList]);

  const cards = useMemo(
    () =>
      morphemeList.slice(0, visibleCount).flatMap((morpheme) => {
        const card = cardByMorpheme.get(morpheme);
        return card == null ? [] : [card];
      }),
    [cardByMorpheme, morphemeList, visibleCount],
  );

  const totalCountRef = useLatest(totalCount);
  const revealMore = useCallback(() => {
    setRevealedCount((count) =>
      count >= totalCountRef.current ? count : count + MORPHEME_CARDS_PAGE_SIZE,
    );
  }, [totalCountRef]);

  return {
    cards,
    hasMore: visibleCount < totalCount,
    isPending: totalCount > 0 && loadedCount < visibleCount,
    revealMore,
  };
}
